<script setup lang="ts">
  import { computed, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { VisitorType } from '@/shared/types/store.types'
  import { useIsHolder } from '@/shared/composables/useIsHolder'
  import { setFormatDate } from '@/shared/utils'
  import { GENDERS, MAX_VISITORS } from '@/shared/constants/other.constants'
  import { useCoreStore } from '@/shared/store'
  import BaseAccordion from '@/shared/components/rebranding/atoms/BaseAccordion.vue'
  import AlertQC from '@qicgroupdoha/qic-connect-ds/components/AlertQC'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  import CellQC from '@qicgroupdoha/qic-connect-ds/components/CellQC'
  import RemoveWindow from '@/shared/components/rebranding/RemoveWindow'
  import { useFeaturesStore } from '@/shared/store/feature'

  interface Props {
    visitors: VisitorType[]
  }
  interface Emits {
    (e: 'add'): void
    (e: 'edit', value: VisitorType): void
    (e: 'delete', value: VisitorType): void
    (e: 'expand', value: VisitorType): void
    (e: 'openRemoveWindowEvent', value: VisitorType): void
    (e: 'closeRemoveWindowEvent', value: VisitorType): void
  }
  const props = withDefaults(defineProps<Props>(), {})
  const emit = defineEmits<Emits>()
  const { t, locale } = useI18n()
  const featuresStore = useFeaturesStore()
  const coreStore = useCoreStore()

  const showRemoveWindows = ref<Record<string, boolean>>({})
  const nationalitiesMap = computed(() => coreStore.nationalitiesMap)
  const countriesMap = computed(() => coreStore.countriesMap)
  const relationsMap = computed(() => coreStore.relationsMap)
  const isMaritalStatusEnabled = computed(() => featuresStore.isMaritalStatusEnabled)

  const limitReached = props.visitors.length >= MAX_VISITORS

  const formatDate = (val: Date) => setFormatDate(val, locale.value as LocaleType)
  const checkDate = (val: string | Date | null) => {
    if (!val) return '-'
    return formatDate(val instanceof Date ? val : new Date(val))
  }
  const toggleRemoveWindow = (action: 'open' | 'close', visitor: VisitorType) => {
    showRemoveWindows.value[visitor.id] = !showRemoveWindows.value[visitor.id]
    action === 'open'
      ? emit('openRemoveWindowEvent', visitor)
      : emit('closeRemoveWindowEvent', visitor)
  }

  const getVisitorData = (visitor: VisitorType): { title: string; value: string }[] => {
    const val = [
      {
        title: t('qic_user_dob'),
        value: checkDate(visitor.birthDate),
      },
      {
        title: t('qic_user_gender'),
        value: visitor.gender === GENDERS.FEMALE ? t('qic_user_female') : t('qic_user_male'),
      },
      {
        title: t('qlm_nationality'),
        value: nationalitiesMap.value.get(visitor.nationality ?? '')?.desc ?? '-',
      },
      {
        title: t('qlm_country'),
        value: countriesMap.value.get(visitor.country ?? '')?.desc ?? '-',
      },
      {
        title: t('qlm_passport'),
        value: visitor.passport,
      },
      {
        title: t('qlm_passport_exp'),
        value: checkDate(visitor.passportExpiryDate),
      },
      {
        title: t('qlm_phone'),
        value: visitor.phoneInfo.phone,
      },
      {
        title: t('qic_user_contacts_email'),
        value: visitor.email,
      },
    ]

    if (!useIsHolder(visitor.relationship)) {
      val.unshift({
        title: t('qlm_relationship'),
        value: relationsMap.value.get(visitor.relationship)?.desc ?? '-',
      })
    }

    if (visitor.visa) {
      val.push({
        title: t('qlm_visa_number'),
        value: visitor.visa,
      })
    }

    if (visitor.visaValidityDate) {
      val.push({
        title: t('visa_expiry_date'),
        value: checkDate(visitor.visaValidityDate),
      })
    }

    if (visitor.maritalStatus && isMaritalStatusEnabled.value) {
      val.push({
        title: t('visitors.fill_visitors_info.marital_status_placeholder'),
        value: coreStore.getMaritalStatusName(visitor.maritalStatus),
      })
    }
    return val
  }
</script>

<template>
  <div class="quote-insured">
    <div class="list-insured__wrapper">
      <BaseAccordion
        v-for="(visitor, index) of visitors"
        :key="visitor.id"
        class="list-insured__accordion"
        :data-test="`accordion-expand-user-${index}`"
        @accordion-click="emit('expand', visitor)"
      >
        <template #name> {{ visitor.firstName }} {{ visitor.lastName }} </template>
        <template #bottom-desc>
          <AlertQC
            v-if="visitor.durationMatch === 0"
            :title="t('quote.duration_mismatch.header')"
            :subtitle="t('quote.duration_mismatch.annotation')"
            variant="warning"
            with-icon
          />
        </template>
        <template #content>
          <ul class="list-insured__items">
            <CellQC
              v-for="(userData, userDataKey) in getVisitorData(visitor)"
              :key="userDataKey"
              :title="userData.title"
              :value="userData.value"
              :with-border="userDataKey < getVisitorData(visitor).length - 1"
            />
          </ul>
          <Transition name="fade" mode="out-in">
            <div
              v-if="!showRemoveWindows[visitor.id]"
              class="list-insured__controls list-insured__controls--buttons"
            >
              <ButtonQC
                variant="outline"
                size="medium"
                icon="edit"
                data-test="edit-user-button"
                @click="emit('edit', visitor)"
              >
                {{ $t('quote.persons_details.edit') }}
              </ButtonQC>

              <ButtonQC
                v-if="!useIsHolder(visitor.relationship)"
                variant="outline"
                size="medium"
                icon="delete-bin"
                data-test="remove-user-button"
                @click="toggleRemoveWindow('open', visitor)"
              >
                {{ $t('qlm_delete') }}
              </ButtonQC>
            </div>
            <RemoveWindow
              v-else
              class="list-insured__controls"
              @cancel="toggleRemoveWindow('close', visitor)"
              @delete="emit('delete', visitor)"
            />
          </Transition>
        </template>
      </BaseAccordion>
    </div>

    <div class="list-insured__bottom">
      <ButtonQC
        variant="secondary"
        size="large"
        icon="add-circle"
        data-test="add-user-button"
        :disabled="limitReached"
        @click="emit('add')"
      >
        {{ $t('qlm_add_visitor') }}
      </ButtonQC>
      <p v-if="limitReached" class="text-medium">
        {{ t('quote.persons_details.add_annotation') }}
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @use '@scss/utils.scss';
  .list-insured__bottom {
    display: flex;
    flex-direction: column;
    padding-top: var(--spacing-7);
    gap: var(--spacing-5);

    p {
      color: var(--colorTxtSrfSecondary);
      text-align: center;
    }
  }

  .list-insured__items {
    display: flex;
    flex-direction: column;

    & + .list-insured__controls {
      margin-top: var(--spacing-7);
    }
  }

  .list-insured__controls--buttons {
    display: flex;
    gap: var(--spacing-3);

    & > button {
      flex: 1;
    }
  }

  .fade {
    &-enter-active,
    &-leave-active {
      transition: opacity 0.15s ease;
    }

    &-enter-from,
    &-leave-to {
      opacity: 0;
    }
  }
</style>

<style lang="scss">
  @use '@scss/utils.scss';

  .list-insured__accordion {
    border-bottom: 1px solid var(--colorBrdrSrfTertiary);

    .accordion-header {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-3);
      padding-block: var(--spacing-7);
      cursor: pointer;
    }

    .accordion-header__wrapper {
      display: flex;
      align-items: center;
      gap: var(--spacing-3);
    }

    .accordion-content {
      padding-inline-start: var(--spacing-8);
      padding-bottom: var(--spacing-7);

      @include utils.mobile-flow {
        padding-inline: 0;
        padding-block: 0 16px;
      }
    }
  }
</style>
